import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Shield, CheckCircle, LogOut, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function Mandatory2FASetup() {
  const { user, logout, complete2FASetup, setUser } = useAuth();
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('intro'); // intro | setup

  const startSetup = async () => {
    setLoading(true);
    try {
      const res = await api.post('/auth/2fa/setup');
      setSetupData(res.data);
      setStep('setup');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start 2FA setup');
    }
    setLoading(false);
  };

  const verify = async () => {
    if (verifyCode.length !== 6) return toast.error('Enter the 6-digit code');
    setLoading(true);
    try {
      await api.post('/auth/2fa/verify', { code: verifyCode });
      toast.success('Two-factor authentication enabled! Welcome to NYCD Portal.');
      setUser(prev => ({ ...prev, totp_enabled: true }));
      complete2FASetup();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code, try again');
      setVerifyCode('');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="mandatory-2fa-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="font-heading font-black text-3xl tracking-tight">
            NYCD<span className="text-primary ml-1">Portal</span>
          </h1>
        </div>

        <Card className="border-border bg-card shadow-none">
          <CardContent className="p-6 space-y-4">
            {step === 'intro' && (
              <>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                    <Shield className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-sm">Two-Factor Authentication Required</p>
                    <p className="text-xs text-muted-foreground">Hi {user?.name?.split(' ')[0]}, your organization requires 2FA to be set up before you can continue.</p>
                  </div>
                </div>

                <div className="border border-border rounded-md p-3 space-y-2 bg-secondary/30">
                  <p className="text-xs font-semibold">What you&apos;ll need:</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li className="flex items-start gap-2">
                      <Smartphone className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                      An authenticator app on your phone (Google Authenticator, Microsoft Authenticator, or Authy)
                    </li>
                  </ul>
                </div>

                <Button
                  onClick={startSetup}
                  disabled={loading}
                  className="w-full bg-primary text-primary-foreground"
                  data-testid="mandatory-2fa-start-btn"
                >
                  <Shield className="h-4 w-4 mr-1.5" /> {loading ? 'Setting up...' : 'Set Up 2FA Now'}
                </Button>

                <button
                  onClick={logout}
                  className="w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-2 cursor-pointer"
                  data-testid="mandatory-2fa-logout-btn"
                >
                  <LogOut className="h-3 w-3" /> Sign out
                </button>
              </>
            )}

            {step === 'setup' && setupData && (
              <>
                <div className="space-y-2">
                  <p className="text-sm font-heading font-bold text-primary">Step 1: Scan this QR Code</p>
                  <p className="text-xs text-muted-foreground">
                    Open your authenticator app and scan the code below:
                  </p>
                  <div className="flex justify-center py-2">
                    <img
                      src={setupData.qr_code}
                      alt="2FA QR Code"
                      className="w-48 h-48 rounded-md border border-border"
                      data-testid="mandatory-2fa-qr"
                    />
                  </div>
                  <details className="text-xs">
                    <summary className="text-muted-foreground cursor-pointer hover:text-foreground">Can&apos;t scan? Enter key manually</summary>
                    <code className="block mt-1 p-2 bg-background rounded text-xs font-mono break-all select-all" data-testid="mandatory-2fa-manual-key">
                      {setupData.secret}
                    </code>
                  </details>
                </div>

                <div className="space-y-2 pt-2 border-t border-border">
                  <p className="text-sm font-heading font-bold text-primary">Step 2: Enter Verification Code</p>
                  <p className="text-xs text-muted-foreground">
                    Type the 6-digit code from your authenticator app:
                  </p>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="bg-background border-border text-center text-2xl tracking-[0.5em] font-mono"
                    autoComplete="one-time-code"
                    autoFocus
                    data-testid="mandatory-2fa-code-input"
                  />
                  <Button
                    onClick={verify}
                    disabled={loading || verifyCode.length !== 6}
                    className="w-full bg-emerald-500 text-white hover:bg-emerald-600"
                    data-testid="mandatory-2fa-verify-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-1.5" /> {loading ? 'Verifying...' : 'Verify & Continue'}
                  </Button>
                </div>

                <button
                  onClick={logout}
                  className="w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-2 cursor-pointer"
                  data-testid="mandatory-2fa-logout-btn"
                >
                  <LogOut className="h-3 w-3" /> Sign out
                </button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
